import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useUserRole } from '../../../hooks/useUserRole';
import { Card, Button } from '@components/common';
import Icon from '@components/Icon/Icon';
import UserSettings from '../../../components/UserSettings';
import PWAInstallButton from '@components/PWAInstallButton';
import PushNotification from '@components/PushNotification';
import NetworkStatus from '@components/NetworkStatus';
import { useNavigate } from 'react-router-dom';
import api from '@services/api';
import './ProfilePage.scss';

const ProfilePage = () => {
    const { user, logout } = useAuthContext();
    const { t } = useLanguage();
    const { canAccessAdmin } = useUserRole();
    const navigate = useNavigate();

    const [profile] = useState({
        name: user?.name || t('common.notProvided'),
        email: user?.email || t('common.notProvided'),
        nextcloud_id: user?.nextcloud_id || t('common.notProvided'),
        role: user?.role || t('common.notProvided'),
        is_active: user?.is_active || false,
        id: user?.id || null,
        created_at: user?.created_at || null,
        updated_at: user?.updated_at || null
    });

    const [userNames, setUserNames] = useState({});
    const [locationNames, setLocationNames] = useState({});
    const [loadingNames, setLoadingNames] = useState(false);

    // Function to fetch user display name by ID
    const fetchUserName = async (userId) => {
        if (!userId || userNames[userId]) return;

        try {
            const response = await api.users.getById(userId);
            setUserNames(prev => ({
                ...prev,
                [userId]: response.data.name || t('common.notProvided')
            }));
        } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
            setUserNames(prev => ({
                ...prev,
                [userId]: t('common.notProvided')
            }));
        }
    };

    // Function to fetch location name by ID
    const fetchLocationName = async (locationId) => {
        if (!locationId || locationNames[locationId]) return;

        try {
            const response = await api.get(`/locations/${locationId}`);
            setLocationNames(prev => ({
                ...prev,
                [locationId]: response.data.name || t('common.notProvided')
            }));
        } catch (error) {
            console.error(`Failed to fetch location ${locationId}:`, error);
            setLocationNames(prev => ({
                ...prev,
                [locationId]: t('common.notProvided')
            }));
        }
    };

    // Fetch user and location names for permissions
    useEffect(() => {
        if (!user?.permissions || user.permissions.length === 0) return;

        const fetchNames = async () => {
            setLoadingNames(true);

            // Collect unique user and location IDs
            const userIds = [...new Set(
                user.permissions
                    .filter(permission => permission.target_user_id)
                    .map(permission => permission.target_user_id)
            )];

            const locationIds = [...new Set(
                user.permissions
                    .filter(permission => permission.target_location_id)
                    .map(permission => permission.target_location_id)
            )];

            // Fetch all user and location names
            await Promise.all([
                ...userIds.map(fetchUserName),
                ...locationIds.map(fetchLocationName)
            ]);

            setLoadingNames(false);
        };

        fetchNames();
    }, [user?.permissions]);

    const handleLogout = () => {
        logout();
    };

    return (
        <div className="profile-page">
            <div className="profile-header">
                <h1>{t('profile.title')}</h1>
                <p>{t('profile.subtitle')}</p>
            </div>

            {/* Personal Information Section */}
            <Card title={t('profile.personalInfo')}>
                <div className="profile-info">
                    <div className="info-resource">
                        <label>{t('profile.displayName')}</label>
                        <span>{profile.name}</span>
                    </div>
                    <div className="info-resource">
                        <label>{t('profile.email')}</label>
                        <span>{profile.email}</span>
                    </div>
                    <div className="info-resource">
                        <label>{t('profile.nextcloudId')}</label>
                        <span>{profile.nextcloud_id}</span>
                    </div>
                    <div className="info-resource">
                        <label>{t('profile.role')}</label>
                        <span>{profile.role}</span>
                    </div>
                    <div className="info-resource">
                        <label>{t('profile.status')}</label>
                        <span className={`status ${profile.is_active ? 'active' : 'inactive'}`}>
                            {profile.is_active ? t('profile.active') : t('profile.inactive')}
                        </span>
                    </div>
                </div>
            </Card>

            {/* Permissions Section */}
            <Card title={t('profile.permissions')}>
                <div className="permissions-info">
                    {user?.permissions && user.permissions.length > 0 ? (
                        <div className="permissions-list">
                            {user.permissions.map((permission, index) => (
                                <div key={permission.id || index} className="permission-item">
                                    <div className="permission-details">
                                        <span className="permission-name">
                                            {permission.permission_type
                                                ? t(`permission.types.${permission.permission_type}`) || permission.permission_type
                                                : t('common.notProvided')
                                            }
                                        </span>
                                        {permission.notes && (
                                            <span className="permission-description">
                                                {permission.notes}
                                            </span>
                                        )}
                                        {permission.target_location_id && (
                                            <span className="permission-resource">
                                               {
                                                    loadingNames ?
                                                        t('common.loading') :
                                                        locationNames[permission.target_location_id] || t('common.loading')
                                                }
                                            </span>
                                        )}
                                        {permission.target_user_id && (
                                            <span className="permission-resource">
                                                {
                                                    loadingNames ?
                                                        t('common.loading') :
                                                        userNames[permission.target_user_id] || t('common.loading')
                                                }
                                            </span>
                                        )}
                                        <div className="permission-meta">
                                            <span className={`status ${permission.is_active ? 'active' : 'inactive'}`}>
                                                {permission.is_active ? t('permission.active') : t('permission.inactive')}
                                            </span>
                                            {permission.created_at && (
                                                <span className="permission-date">
                                                    {t('permission.createdAt')}: {new Date(permission.created_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-permissions">
                            <Icon icon="info-circle" size="sm" />
                            <span>{t('permission.noPermissions')}</span>
                        </div>
                    )}
                </div>
            </Card>

            {/* Settings Section */}
            <div className="settings-section">
                <UserSettings />

                {/* PWA Install Section */}
                <Card title={t('profile.appSettings')}>
                    <div className="app-settings">
                        <div className="app-setting-item">
                            <div className="setting-description">
                                <h4>{t('profile.installApp')}</h4>
                                <p>{t('profile.installAppDescription')}</p>
                            </div>
                            <PWAInstallButton text={t('profile.installApp')} />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Notification Section */}
            <Card title={t('profile.notifications')}>
                <PushNotification />
            </Card>


            {/* Administrative Section - Only visible to admins */}
            {canAccessAdmin && (
                <Card>
                    <div className="admin-section">
                        <div className="admin-item">
                            <div className="admin-description">
                                <h4>{t('admin.title')}</h4>
                                <p>{t('admin.subtitle')}</p>
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => navigate('/admin')}
                                className="admin-button"
                            >
                                <Icon icon="user-shield" size="sm" />
                                <span>{t('navigation.admin')}</span>
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Logout Section */}
            <div className="profile-actions">
                <button onClick={handleLogout} className="logout-btn">
                    <Icon icon="sign-out-alt" size="sm" />
                    <span>{t('auth.logout')}</span>
                </button>
            </div>

            {/* PWA Components */}
            <NetworkStatus />
        </div>
    );
};

export default ProfilePage;